/* Elin Perfumes — review count roll-up.
 *
 * One counted value drives both halves of the display: the thousands are its
 * quotient, the blurred digits its remainder. Deriving them separately lets
 * the lead reach its final figure while the remainder keeps spinning, which
 * shows numbers above the target before snapping back down.
 *
 * Runs only while on screen, and not at all for reduced-motion users, who
 * keep the final figure the markup already contains.
 */
(() => {
  const START = 1000;
  const RUN_MS = 5000;
  const HOLD_MS = 6000;
  const MAX_BLUR = 5;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('[data-elin-count]').forEach((el) => {
    const raw = (el.dataset.elinCount || el.textContent).trim();
    const parts = raw.match(/^(\D*)([\d.,\s]+)(\D*)$/);
    if (!parts) return;

    const target = parseInt(parts[2].replace(/\D/g, ''), 10);
    if (!Number.isFinite(target) || target < 1000) return;

    const targetThousands = Math.floor(target / 1000);
    const remainder = target % 1000;
    const remainderText = String(remainder).padStart(3, '0');

    const lead = document.createElement('span');
    const spin = document.createElement('span');
    spin.className = 'elin-count__spin';
    lead.textContent = String(targetThousands);
    spin.textContent = remainderText;

    el.textContent = '';
    if (parts[1]) el.append(document.createTextNode(parts[1]));
    el.append(lead, document.createTextNode(','), spin);
    if (parts[3]) el.append(document.createTextNode(parts[3]));

    // Pin the width at the final value so the panel cannot resize mid-run.
    el.style.minWidth = `${Math.ceil(el.getBoundingClientRect().width)}px`;

    if (reduced) return;

    let frame = null;
    let timer = null;
    let running = false;

    const settle = () => {
      lead.textContent = String(targetThousands);
      spin.textContent = remainderText;
      spin.style.filter = '';
    };

    const run = () => {
      const start = performance.now();
      const step = (now) => {
        const p = Math.min(1, (now - start) / RUN_MS);
        // Quadratic rather than cubic ease-out: still fast enough to blur at
        // the start, without stalling on the last thousand for two seconds.
        const eased = 1 - Math.pow(1 - p, 2);
        const value = Math.round(START + (target - START) * eased);

        lead.textContent = String(Math.floor(value / 1000));
        spin.textContent = String(value % 1000).padStart(3, '0');

        if (p < 1) {
          // Tracks the easing's velocity, so the blur clears as the digits
          // slow rather than on a schedule of its own.
          spin.style.filter = `blur(${(MAX_BLUR * (1 - p)).toFixed(2)}px)`;
          frame = requestAnimationFrame(step);
        } else {
          settle();
          timer = setTimeout(run, HOLD_MS);
        }
      };
      frame = requestAnimationFrame(step);
    };

    const stop = () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
      settle();
    };

    if (!('IntersectionObserver' in window)) {
      run();
      return;
    }

    new IntersectionObserver(
      (entries) => {
        const visible = entries[0].isIntersecting;
        if (visible && !running) {
          running = true;
          run();
        } else if (!visible && running) {
          running = false;
          stop();
        }
      },
      { threshold: 0.4 }
    ).observe(el);
  });
})();
